import { ImagePlus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { ApiError, uploadFile } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { Button } from './ui';

export interface LogoValue {
  id: string;
  /** A signed link, good for an <img> without a session. */
  url: string;
}

/** The longest side a logo is kept at: sharp at the size a PDF prints it, and quick to upload. */
const MAX_SIDE = 1200;
/** What the API takes for a logo (domain/file-policy.ts, MAX_LOGO_BYTES). */
const MAX_MB = 2;
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * A client's logo on its form (operator, 2026-10-09), printed at the top of
 * its contracts' PDFs opposite ours.
 *
 * A PDF takes PNG and JPEG pictures only, so whatever the browser can read —
 * PNG, JPG, WebP, a GIF's first frame — is redrawn here as a PNG, no larger
 * than it needs to be, before it is uploaded. A PNG keeps a transparent
 * background transparent. The file is uploaded as soon as it is picked; the
 * client form saves which one it is.
 */
export function LogoPicker({
  value,
  onChange,
  clientName,
}: {
  value: LogoValue | null;
  onChange: (logo: LogoValue | null) => void;
  clientName: string;
}) {
  const { t } = useI18n();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const errorText = (e: unknown): string => {
    if (e instanceof ApiError) {
      if (['FILE_STORAGE_UNREACHABLE', 'FILE_STORAGE_REFUSED', 'FILE_STORAGE_UNAVAILABLE'].includes(e.code)) {
        return t('clients.logoStorage');
      }
      if (e.code === 'FILE_TOO_LARGE') return t('clients.logoTooLarge', { mb: MAX_MB });
    }
    return t('clients.logoRefused', { reason: e instanceof Error ? e.message : String(e) });
  };

  const pick = async (file: File) => {
    setError(null);
    setBusy(true);
    setProgress(0);
    try {
      const png = await redraw(file);
      if (!png) return setError(t('clients.logoUnreadable'));
      if (png.size > MAX_MB * 1024 * 1024) return setError(t('clients.logoTooLarge', { mb: MAX_MB }));
      const uploaded = await uploadFile(png, `${baseName(file.name) || 'logo'}.png`, { onProgress: setProgress });
      onChange({ id: uploaded.id, url: uploaded.url });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4">
        {/* White, like the page it is printed on: a dark logo stays visible in the dark theme. */}
        <div className="grid h-20 w-44 shrink-0 place-items-center overflow-hidden rounded-md border hairline bg-white p-2">
          {value ? (
            <img src={value.url} alt={t('clients.logoAlt', { client: clientName })} className="max-h-full max-w-full object-contain" />
          ) : (
            <ImagePlus className="h-6 w-6 text-neutral-400" aria-hidden />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            icon={<ImagePlus className="h-4 w-4" aria-hidden />}
            loading={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? t('clients.logoUploading') : value ? t('clients.logoReplace') : t('clients.logoAdd')}
          </Button>
          {value && !busy && (
            <Button variant="ghost" icon={<Trash2 className="h-4 w-4" aria-hidden />} onClick={() => onChange(null)}>
              {t('clients.logoRemove')}
            </Button>
          )}
          {busy && progress > 0 && <span className="text-caption tabular text-muted">{Math.round(progress * 100)}%</span>}
        </div>
      </div>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void pick(file);
        }}
      />
      {error ? (
        <p className="mt-1.5 text-caption text-error-fg dark:text-error-dark" role="alert">{error}</p>
      ) : (
        <p className="mt-1.5 text-caption text-faint">{t('clients.logoHint')}</p>
      )}
    </div>
  );
}

/** The picture redrawn as a PNG no wider or taller than MAX_SIDE, or null when the browser cannot read it. */
async function redraw(file: File): Promise<Blob | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
}

/** "Acme logo (final).webp" → "Acme logo (final)": the PNG keeps the name. */
function baseName(name: string): string {
  return name.replace(/\.[^.]+$/, '').trim();
}
